import { useTranslation } from '@thoughtspot/i18n';
import { Accordion, AccordionVariant, } from '@thoughtspot/radiant-react/widgets/accordion';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { FormControl } from '@thoughtspot/radiant-react/widgets/form-control';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Select } from '@thoughtspot/radiant-react/widgets/select';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area/text-area';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { PEMObject } from 'pem-ts';
import React, { useEffect, useState } from 'react';
import { useGetAttributeMappingQuery, useGetSamlConnectionQuery, useUploadMetatDataMutation, } from "../../../services/saml/saml-service";
import { algoTypes, bindingList, getDefaultSamlHeaders, getSubDomainIfOrgEnabled, IDP_TYPE, TranslationsKeys, } from '../constant';
import styles from './identity-details.module.scss';
const BEGIN_CERTIFICATE_TEXT = '-----BEGIN CERTIFICATE-----';
const END_CERTIFICATE_TEXT = '-----END CERTIFICATE-----';
const MAX_ALLOWED_SIZE = 500000;
export const IdentityDetails = ({ onProviderForm, setScreenLoader, editData, edit, clientId, formDataRef, resetEdit, setAttributeValues, currentOrgInfo, idpWithNameExists, showHideBanner, }) => {
    const { t } = useTranslation();
    const getTranslations = (key, args) => t(`${key}`, args);
    const [metaDataField, setMetaDataField] = useState(false);
    const [metaDataFile, setMetaDataFile] = useState();
    const [metaDataInputError, setMetaDataInputError] = useState(false);
    const [samlData, setSamlData] = useState({});
    const [certFileError, setCertFileError] = useState(false);
    const [certText, setCertText] = useState('');
    const [noNameError, setNoNameError] = useState(false);
    const [noIssuer, setNoIssuer] = useState(false);
    const [noSignOnURL, setNoSignOnURL] = useState(false);
    const [hideForm, setHideForm] = useState(false);
    const onError = (error) => {
        setMetaDataInputError(true);
    };
    const [uploadMetadataFile] = useUploadMetatDataMutation((response) => {
    }, (error) => onError(error), metaDataFile);
    const { data, loading, error } = useGetSamlConnectionQuery({
        clientId,
        id: edit && editData ? editData.id : '',
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const { data: mapData, loading: mapLoading, error: mapError, } = useGetAttributeMappingQuery({
        id: edit && editData ? editData.id : '',
        clientId,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const textInputBox = (testId, label, value, onChangeCall) => (React.createElement(Input.Text, { "data-testid": testId, disabled: false, ariaLabel: label, value: value, inputClassName: styles.inputField, onChange: onChangeCall }));
    const getInputComponent = ({ testId, type, onChangeCall, value, label, ...item }) => {
        switch (type) {
            case 'number':
                return (React.createElement(Input.Number, { "data-testid": testId, disabled: false, ariaLabel: label, value: value || '', className: styles.fileInput, onChange: onChangeCall }));
            case 'file':
                return (React.createElement(Input.File, { "data-testid": testId, className: styles.fileInput, onChange: onChangeCall, disabled: item.disabled ? item.disabled : false }));
            case 'dropdown':
                return (React.createElement(Select.Dropdown, { "data-testid": testId, id: item.id, disabled: false, optionsGroups: item.groupList, classList: {
                        selectHeadClass: styles.inputField,
                    }, selectedOptions: [item.selectedOptions], onChange: onChangeCall, dropdownWidth: "targetWidth" }));
            case 'text':
                return textInputBox(testId, label, value, onChangeCall);
            default:
                return textInputBox(testId, label, value, onChangeCall);
        }
    };
    const updateSamlData = () => {
        let subDomain = getSubDomainIfOrgEnabled();
        if (!subDomain && currentOrgInfo) {
            subDomain = `${currentOrgInfo.orgName}_`;
        }
        const { name, sso, issuer, certData, requestSignatureAlgorithm, responseSignatureAlgorithm, maxClockSkew, ...rest } = formDataRef.current;
        setSamlData({
            name: subDomain ? `${subDomain}${name}` : name,
            type: 'SAML2',
            status: 'ACTIVE',
            sso: {
                url: sso.url,
                binding: sso.binding,
            },
            issuer,
            certData: {
                x5c: certData.x5c,
            },
            requestSignatureAlgorithm,
            responseSignatureAlgorithm,
            maxClockSkew,
            ...rest,
        });
    };
    const handleSubmit = (event) => {
        event.preventDefault();
        formDataRef.current.type = IDP_TYPE.SAML;
        if (noNameError || certFileError || noIssuer || noSignOnURL) {
            return;
        }
        const { name, sso, issuer, certData } = formDataRef.current;
        if (idpWithNameExists(name, formDataRef.current.id)) {
            const message = TranslationsKeys.DUPLICATE_NAME_ERROR;
            showHideBanner(getTranslations(message), true);
            return;
        }
        if (name && certData.x5c && issuer && sso.url) {
            updateSamlData();
            setHideForm(true);
        }
        else {
            if (!name)
                setNoNameError(true);
            if (!certData.x5c)
                setCertFileError(true);
            if (!issuer)
                setNoIssuer(true);
            if (!sso.url)
                setNoSignOnURL(true);
        }
    };
    const metaDatafieldBox = (event) => {
        setMetaDataField(!metaDataField);
    };
    const handleXmlFileChange = ({ target }) => {
        const formData = new FormData();
        if (target.files[0].type === 'text/xml') {
            setMetaDataInputError(false);
            setMetaDataFile(formData);
        }
        else {
            setMetaDataInputError(true);
        }
    };
    const validateCertificate = async (certificateText) => {
        try {
            const pem = new PEMObject();
            pem.decode(certificateText);
            return true;
        }
        catch {
            return false;
        }
    };
    const onFileOnLoad = async (pemText) => {
        let x5cValue;
        if (pemText.indexOf('BEGIN CERTIFICATE') !== -1) {
            const isValid = await validateCertificate(pemText);
            if (isValid) {
                x5cValue = pemText.slice(27);
                if (x5cValue.indexOf(END_CERTIFICATE_TEXT) !== -1) {
                    x5cValue = x5cValue.replace(END_CERTIFICATE_TEXT, '');
                }
                else {
                    x5cValue = x5cValue.slice(0, -26);
                }
                formDataRef.current.certData.x5c = x5cValue.replace(/[\r\n]+/gm, '');
                certFileError && setCertFileError(false);
                setCertText(pemText);
            }
            else {
                setCertText('');
                setCertFileError(true);
            }
        }
        else {
            setCertText('');
            setCertFileError(true);
        }
    };
    const certificateFileUpload = (event) => {
        const certFile = event.target.files[0];
        if (certFile.size < MAX_ALLOWED_SIZE) {
            const reader = new FileReader();
            reader.readAsText(certFile);
            reader.onload = () => {
                let pemText = reader.result;
                pemText = pemText.toString();
                onFileOnLoad(pemText);
            };
            reader.onerror = () => setCertFileError(true);
            setCertFileError(false);
        }
        else {
            setCertText('');
            setCertFileError(true);
        }
    };
    const onChangeCertificate = (event) => {
        const { value } = event.target;
        onFileOnLoad(value);
    };
    const setMandatoryFieldsToDefault = (name = '', issuer = '', x5c = '') => {
        const ssoEdited = {
            url: '',
            binding: 'POST',
        };
        formDataRef.current.name = name;
        formDataRef.current.issuer = issuer;
        formDataRef.current.sso = ssoEdited;
        formDataRef.current.certData.x5c = x5c;
    };
    useEffect(() => {
        if (metaDataFile)
            uploadMetadataFile();
    }, [metaDataFile]);
    useEffect(() => {
        if (!error && !loading && data) {
            if (edit) {
                let subDomain = getSubDomainIfOrgEnabled();
                if (!subDomain && currentOrgInfo) {
                    subDomain = `${currentOrgInfo.orgName}_`;
                }
                const { id, name, issuer, sso, certData: { x5c }, requestSignatureAlgorithm, responseSignatureAlgorithm, maxClockSkew, } = data.Data;
                setCertText(`${BEGIN_CERTIFICATE_TEXT}\n${x5c}\n${END_CERTIFICATE_TEXT}`);
                formDataRef.current = {
                    ...formDataRef.current,
                    id,
                    name: subDomain ? name.replaceAll(subDomain, '') : name,
                    issuer,
                    sso,
                    certData: { x5c },
                    requestSignatureAlgorithm,
                    responseSignatureAlgorithm,
                    maxClockSkew,
                };
            }
        }
    }, [data, error, loading]);
    useEffect(() => {
        if (!mapLoading && !mapError && mapData && edit) {
            setAttributeValues(mapData.Data);
        }
    }, [mapData, mapError, mapLoading]);
    useEffect(() => {
        if (edit && !(loading || mapLoading)) {
            if ((mapData || mapError) && (data || error)) {
                setScreenLoader(false);
            }
        }
    }, [mapLoading, loading, mapData, data, error, mapError]);
    useEffect(() => {
        if (edit) {
            setScreenLoader(true);
        }
        if (formDataRef.current.certData.x5c) {
            setCertText(`${BEGIN_CERTIFICATE_TEXT}\n${formDataRef.current.certData.x5c}\n${END_CERTIFICATE_TEXT}`);
        }
    }, []);
    useEffect(() => {
        if (Object.keys(samlData).length > 0) {
            onProviderForm(false, false, true, null);
        }
    }, [samlData]);
    const cancelChange = () => {
        setMandatoryFieldsToDefault();
        onProviderForm(true, false, false);
        resetEdit();
    };
    return (React.createElement(Vertical, { className: hideForm ? styles.hide : '', spacing: "c", width: 400 },
        React.createElement(Typography, { variant: "h3", className: styles.marginTop32 }, getTranslations(TranslationsKeys.DETAILS_HEADER)),
        React.createElement("form", { onSubmit: handleSubmit },
            React.createElement(FormControl, { label: getTranslations(TranslationsKeys.NAME_LABEL), hasError: noNameError, helpText: getTranslations(TranslationsKeys.NAME_LABEL_HELPTEXT) }, getInputComponent({
                testId: 'ConnectionNameTestId',
                type: 'text',
                label: '',
                value: formDataRef.current.name,
                onChangeCall: (event) => {
                    formDataRef.current.name = event.target.value;
                    setNoNameError(false);
                },
            })),
            React.createElement(FormControl, { label: getTranslations(TranslationsKeys.METADATA_LABEL), hasError: metaDataInputError, helpText: getTranslations(TranslationsKeys.METADATA_LABEL_HELPTEXT), className: metaDataField ? '' : styles.hide }, getInputComponent({
                testId: 'metaDataInputTestId',
                type: 'file',
                onChangeCall: (event) => {
                    handleXmlFileChange(event);
                },
            })),
            React.createElement(FormControl, { label: getTranslations(TranslationsKeys.CERTIFICATE_LABEL), hasError: certFileError, disabled: !!metaDataField, className: styles.certificateInputFileField }, getInputComponent({
                testId: 'certificateInputTestId',
                type: 'file',
                disabled: !!metaDataField,
                onChangeCall: (event) => {
                    certificateFileUpload(event);
                },
            })),
            React.createElement(FormControl, { label: "", hasError: certFileError, helpText: certFileError
                    ? getTranslations(TranslationsKeys.CERTIFICATE_ERROR_MSG)
                    : getTranslations(TranslationsKeys.CERTIFICATE_LABEL_HELPTEXT), disabled: false },
                React.createElement(TextArea, { ariaLabel: getTranslations(TranslationsKeys.CERTIFICATE_LABEL), value: certText, onChange: (event) => {
                        onChangeCertificate(event);
                    }, className: styles.certTextarea, disabled: false, "data-testid": "cert-text-area", rows: 8 })),
            React.createElement(FormControl, { label: getTranslations(TranslationsKeys.ISSUER_LABEL), hasError: noIssuer, helpText: getTranslations(TranslationsKeys.ISSUER_LABEL_HELPTEXT), disabled: !!metaDataField }, getInputComponent({
                testId: 'issuerTestId',
                type: 'text',
                value: formDataRef.current.issuer,
                onChangeCall: (event) => {
                    formDataRef.current.issuer = event.target.value;
                    setNoIssuer(false);
                },
            })),
            React.createElement(FormControl, { label: getTranslations(TranslationsKeys.SSO_LABEL), hasError: noSignOnURL, helpText: getTranslations(TranslationsKeys.SSO_LABEL_HELPTEXT), disabled: !!metaDataField }, getInputComponent({
                testId: 'ssoTestId',
                type: 'text',
                value: formDataRef.current.sso.url,
                onChangeCall: (event) => {
                    setNoSignOnURL(false);
                    formDataRef.current.sso.url = event.target.value;
                },
            })),
            React.createElement(Vertical, { spacing: "e" },
                React.createElement(Horizontal, null,
                    React.createElement(Accordion, { className: styles.advanceConfigContainer, expandMultipleItems: true, variant: AccordionVariant.SIDE_NAV },
                        React.createElement(Accordion.Item, { header: getTranslations(TranslationsKeys.ADVANCED_CONFIG), headerClassName: styles.advanceConfigTitle, className: styles.accordionItem },
                            React.createElement(Vertical, { spacing: "c", width: 400 },
                                React.createElement(FormControl, { label: getTranslations(TranslationsKeys.BINDING_LABEL), helpText: getTranslations(TranslationsKeys.BINDING_LABEL_HELPTEXT) }, getInputComponent({
                                    testId: 'bindingTestId',
                                    type: 'dropdown',
                                    groupList: bindingList,
                                    id: 'requestBindingSelect',
                                    selectedOptions: formDataRef.current.sso.binding,
                                    onChangeCall: (event) => {
                                        formDataRef.current.sso.binding =
                                            event.label;
                                    },
                                })),
                                React.createElement(FormControl, { label: getTranslations(TranslationsKeys.REQ_ALGO_LABEL), helpText: getTranslations(TranslationsKeys.REQ_ALGO_LABEL_HELPTEXT) }, getInputComponent({
                                    testId: 'reqAlgoTestId',
                                    type: 'dropdown',
                                    groupList: algoTypes,
                                    id: 'requestSignAlgo',
                                    selectedOptions: formDataRef.current
                                        .requestSignatureAlgorithm,
                                    onChangeCall: (event) => {
                                        formDataRef.current.requestSignatureAlgorithm =
                                            event.label;
                                    },
                                })),
                                React.createElement(FormControl, { label: getTranslations(TranslationsKeys.RES_ALGO_LABEL), helpText: getTranslations(TranslationsKeys.RES_ALGO_LABEL_HELPTEXT) }, getInputComponent({
                                    testId: 'resAlgoTestId',
                                    type: 'dropdown',
                                    groupList: algoTypes,
                                    id: 'responseSignAlgo',
                                    selectedOptions: formDataRef.current
                                        .responseSignatureAlgorithm,
                                    onChangeCall: (event) => {
                                        formDataRef.current.responseSignatureAlgorithm =
                                            event.label;
                                    },
                                })),
                                React.createElement(FormControl, { label: getTranslations(TranslationsKeys.SKEWTIME_LABEL), helpText: getTranslations(TranslationsKeys.SKEWTIME_LABEL_HELPTEXT) }, getInputComponent({
                                    testId: 'maxClockTestId',
                                    type: 'text',
                                    label: '',
                                    value: `${formDataRef.current.maxClockSkew}`,
                                    onChangeCall: ({ target, }) => {
                                        formDataRef.current.maxClockSkew = Number(target.value);
                                    },
                                }))))))),
            React.createElement(Horizontal, { spacing: "e", hAlignContent: "left" },
                React.createElement(Vertical, null,
                    React.createElement(Button, { isDisabled: loading || mapLoading, text: getTranslations(TranslationsKeys.CONTINUE), onClick: handleSubmit })),
                React.createElement(Vertical, null,
                    React.createElement(Button, { type: "secondary", "data-testid": "idpFormCancelButton", text: getTranslations(TranslationsKeys.CANCEL), onClick: cancelChange }))))));
};
//# sourceMappingURL=identity-details.container.js.map